import '../../_proto/landscope-map-viewer_pb';
import { grpcClientFactory } from './grpcClientFactory';
class DemoProjectsService {
    constructor() {
        // Create client with conditional auth interceptor
        this.client = grpcClientFactory.createDemoProjectsClient({ withConditionalAuth: true });
    }
    /**
     * Clear any cached client state (useful when user logs out)
     */
    clearClient() {
        // Recreate client to clear any cached state
        this.client = grpcClientFactory.createDemoProjectsClient({ withConditionalAuth: true });
    }
    async getDemoProjects() {
        return new Promise((resolve, reject) => {
            const request = new proto.google.protobuf.Empty();
            this.client.getDemoProjects(request, {}, (err, response) => {
                if (err) {
                    console.error('Demo projects fetch error:', err);
                    reject(err);
                }
                else if (response) {
                    resolve(response);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    async getProjectStructure(projectId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.GetProjectStructureRequest();
            request.setProjectId(projectId);
            this.client.getProjectStructure(request, {}, (err, response) => {
                if (err) {
                    console.error('Demo project structure fetch error:', err);
                    reject(err);
                }
                else if (response) {
                    resolve(response);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    async getArtifact(artifactId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.GetArtifactRequest();
            request.setArtifactId(artifactId);
            this.client.getArtifact(request, {}, (err, response) => {
                if (err) {
                    console.error('Demo artifact fetch error:', err);
                    reject(err);
                }
                else if (response) {
                    resolve(response);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    async getProjectStatistics(projectId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.GetProjectStatisticsRequest();
            request.setProjectId(projectId);
            this.client.getProjectStatistics(request, {}, (err, response) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
                if (err) {
                    console.error('Demo project statistics fetch error:', err);
                    reject(err);
                }
                else if (response) {
                    try {
                        // Map polygons
                        const toLatLng = (coord) => ({ lat: coord.getLatitude(), lng: coord.getLongitude() });
                        const propertyPolygonList = ((_a = response.getPropertyPolygonList) === null || _a === void 0 ? void 0 : _a.call(response)) || [];
                        const bufferPolygonList = ((_b = response.getBufferPolygonList) === null || _b === void 0 ? void 0 : _b.call(response)) || [];
                        const propertyPolygon = propertyPolygonList.map(toLatLng);
                        const bufferPolygon = bufferPolygonList.map(toLatLng);
                        // Map breakdowns
                        const mapItem = (item) => {
                            var _a, _b, _c, _d, _e;
                            return ({
                                key: (_a = item.getKey) === null || _a === void 0 ? void 0 : _a.call(item),
                                title: (_b = item.getTitle) === null || _b === void 0 ? void 0 : _b.call(item),
                                color: (_c = item.getColor) === null || _c === void 0 ? void 0 : _c.call(item),
                                description: (_d = item.getDescription) === null || _d === void 0 ? void 0 : _d.call(item),
                                percent: (_e = item.getPercent) === null || _e === void 0 ? void 0 : _e.call(item)
                            });
                        };
                        const aspectBreakdown = (((_c = response.getAspectBreakdownList) === null || _c === void 0 ? void 0 : _c.call(response)) || []).map(mapItem);
                        const slopeBreakdown = (((_d = response.getSlopeBreakdownList) === null || _d === void 0 ? void 0 : _d.call(response)) || []).map(mapItem);
                        const elevationBreakdown = (((_e = response.getElevationBreakdownList) === null || _e === void 0 ? void 0 : _e.call(response)) || []).map(mapItem);
                        const geomorphonsBreakdown = (((_f = response.getGeomorphonsBreakdownList) === null || _f === void 0 ? void 0 : _f.call(response)) || []).map(mapItem);
                        // Numbers - normalize zero to undefined to better reflect availability
                        const normalizeNum = (n) => (typeof n === 'number' && n !== 0 ? n : undefined);
                        const maxHeightM = normalizeNum((_g = response.getMaxHeightM) === null || _g === void 0 ? void 0 : _g.call(response));
                        const minHeightM = normalizeNum((_h = response.getMinHeightM) === null || _h === void 0 ? void 0 : _h.call(response));
                        const altitudeStdDev = normalizeNum((_j = response.getAltitudeStdDev) === null || _j === void 0 ? void 0 : _j.call(response));
                        const name = ((_k = response.getName) === null || _k === void 0 ? void 0 : _k.call(response)) || undefined;
                        const description = ((_l = response.getDescription) === null || _l === void 0 ? void 0 : _l.call(response)) || undefined;
                        const stats = {
                            name,
                            description,
                            propertyPolygon: propertyPolygon.length >= 3 ? propertyPolygon : undefined,
                            bufferPolygon: bufferPolygon.length >= 3 ? bufferPolygon : undefined,
                            maxHeightM,
                            minHeightM,
                            altitudeStdDev,
                            aspectBreakdown: aspectBreakdown.length > 0 ? aspectBreakdown : undefined,
                            slopeBreakdown: slopeBreakdown.length > 0 ? slopeBreakdown : undefined,
                            elevationBreakdown: elevationBreakdown.length > 0 ? elevationBreakdown : undefined,
                            geomorphonsBreakdown: geomorphonsBreakdown.length > 0 ? geomorphonsBreakdown : undefined
                        };
                        resolve(stats);
                    }
                    catch (mapErr) {
                        console.error('Error mapping demo project statistics:', mapErr);
                        resolve(null);
                    }
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    async getSupportedExportFormats(projectId, layerName) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.GetSupportedExportFormatsRequest();
            request.setProjectId(projectId);
            request.setLayerName(layerName);
            this.client.getSupportedExportFormats(request, {}, (err, response) => {
                if (err) {
                    console.error('Demo project supported formats fetch error:', err);
                    reject(err);
                }
                else if (response) {
                    resolve(response);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    async exportArtifact(artifactId, format) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.ExportArtifactRequest();
            request.setArtifactId(artifactId);
            request.setFormat(format);
            // Create options message (reserved for future use)
            const exportOptions = new proto.landscope.mapviewer.v1.ExportOptions();
            request.setOptions(exportOptions);
            this.client.exportArtifact(request, {}, (err, response) => {
                if (err) {
                    console.error('Demo artifact export error:', err);
                    reject(err);
                }
                else if (response) {
                    const result = {
                        success: response.getSuccess(),
                        message: response.getMessage() || '',
                        downloadUrl: response.getDownloadUrl() || undefined,
                        downloadId: response.getDownloadId() || undefined
                    };
                    resolve(result);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    async createProjectDownload(projectId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.CreateProjectDownloadRequest();
            request.setProjectId(projectId);
            this.client.createProjectDownload(request, {}, (err, response) => {
                if (err) {
                    console.error('Demo project download error:', err);
                    reject(err);
                }
                else if (response) {
                    const downloadId = response.getDownloadId();
                    if (!downloadId) {
                        reject(new Error('No download ID received'));
                        return;
                    }
                    resolve(downloadId);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    async getProjectDownloadStatus(downloadId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.GetProjectDownloadStatusRequest();
            request.setDownloadId(downloadId);
            this.client.getProjectDownloadStatus(request, {}, (err, response) => {
                if (err) {
                    console.error('Demo project download status error:', err);
                    reject(err);
                }
                else if (response) {
                    const result = {
                        status: response.getStatus(),
                        presignedUrl: response.getPresignedUrl() || undefined,
                        error: response.getError() || undefined,
                        expiresAt: response.getExpiresAt() || undefined
                    };
                    resolve(result);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
}
// Create singleton instance
export const demoProjectsService = new DemoProjectsService();
